import React from 'react'
import { Box, Divider, Typography } from '@mui/material'
import { useApplicationSuiteContext } from '../../../contexts/ApplicationSuiteProvider'
import SectionTitle from '../SectionTitle'
import SettingsRow from '../SettingsRow'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import { getClientTimeZone } from '../../../common/timezones'
import { normalizeSchedule, convertScheduleTz } from '../../../common/backtestSchedule'

dayjs.extend(customParseFormat)

function BacktestSettings({ backtest }: any) {
  const { usermeta, canShowcaseViewBacktestSettings } = useApplicationSuiteContext()

  if (!backtest) {
    return null
  }

  const pattern = backtest.pattern
  const { name, version } = pattern
  const { executionSymbol, dateRange } = backtest.backtestSettings
  const trigger =
    pattern &&
    `${pattern.trigger.chartDetails.periodicity} ${pattern.trigger.chartDetails.timeframe} at post`
  const ownsBacktest = backtest.gcid === usermeta.gcid

  const viewerTz = getClientTimeZone()
  const normalizedSchedule = normalizeSchedule(backtest.backtestSettings)
  const localSchedule = convertScheduleTz(normalizedSchedule, viewerTz, dateRange?.startDate)

  const dateRangeText = `${new Date(dateRange.startDate).toDateString()} to ${new Date(dateRange.endDate).toDateString()}`

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <SectionTitle title="Backtest Settings" />
      </Box>
      <Box>
        <SettingsRow
          title="Pattern"
          data={
            canShowcaseViewBacktestSettings || ownsBacktest
              ? `${name} v${version}`
              : 'Backtest settings not available'
          }
        />
        <SettingsRow
          title="Symbol"
          data={
            canShowcaseViewBacktestSettings || ownsBacktest
              ? executionSymbol
              : 'Backtest settings not available'
          }
        />
        <SettingsRow
          title="Trigger"
          data={
            canShowcaseViewBacktestSettings || ownsBacktest
              ? trigger
              : 'Backtest settings not available'
          }
        />
        <SettingsRow
          title="Date Range"
          data={
            canShowcaseViewBacktestSettings || ownsBacktest
              ? dateRangeText
              : 'Backtest settings not available'
          }
        />
        <SettingsRow
          title="Schedule"
          data={
            canShowcaseViewBacktestSettings || ownsBacktest ? (
              <ScheduleDisplay windows={localSchedule.windows} />
            ) : (
              'Backtest settings not available'
            )
          }
        />
      </Box>
      <Divider />
    </Box>
  )
}

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

function formatTime(hhmm: string): string {
  if (hhmm === '24:00') return 'End of Day'
  const d = dayjs(hhmm, 'HH:mm')
  return d.isValid() ? d.format('h:mm A') : hhmm
}

function windowTimeLabel(win: { startTime: string; endTime: string }): string {
  if (win.startTime === win.endTime || (win.startTime === '00:00' && win.endTime === '24:00')) {
    return 'All day'
  }
  return `${formatTime(win.startTime)} – ${formatTime(win.endTime)}`
}

function ScheduleDisplay({
  windows,
}: {
  windows: Array<{ dayOfWeek: number; startTime: string; endTime: string }>
}) {
  if (windows.length === 0) return <Box>No schedule restriction</Box>

  const byDay: Record<number, typeof windows> = {}
  windows.forEach((win) => {
    if (!byDay[win.dayOfWeek]) byDay[win.dayOfWeek] = []
    byDay[win.dayOfWeek].push(win)
  })

  return (
    <Box display="flex" flexDirection="column" gap={0.5}>
      {Object.keys(byDay)
        .map(Number)
        .sort()
        .map((day) => (
          <Box key={day}>
            <Typography variant="body2" fontWeight={600}>
              {DAY_LABELS[day]}
            </Typography>
            {byDay[day].map((win, i) => (
              <Typography key={i} variant="body2" sx={{ pl: 1, color: 'text.secondary' }}>
                {windowTimeLabel(win)}
              </Typography>
            ))}
          </Box>
        ))}
    </Box>
  )
}

export default BacktestSettings
